// ReplayButton (UI-C5, P-8, D-11, T-304): in the bubble's META ROW since the D-31 amendment — a line row holds
// the phrase alone, so the words get the full width — in the seat colour, a drawn person with a waveform (never
// the speaker glyph: that is UI-C6, voicing the translation). ONE per landed bubble, playing the original audio
// whether or not a translation shows; a live or failed bubble has none. A real button; its states (consultant
// 2026-09-20): idle, loading, playing, unavailable, unsupported, plus error for REPLAY_ERROR_MS after a failed try.
//  - disabled until audio_ready for THIS phrase ("waiting");
//  - idle: named "Replay"; loading and playing: named "Stop" (a tap stops it), aria-pressed=true, aria-busy while it
//    loads; a tap after a stop plays it again from the start;
//  - unavailable (every refusal is a 404): disabled, no visible words, named "Replay unavailable";
//  - unsupported (the one-time canPlayType probe said ""): disabled, with the localised reason ("a newer iOS" on iOS,
//    "a newer browser" elsewhere) on its OWN row under the meta row (ReplayReason), tied to the button by
//    aria-describedby — the two are siblings no longer, so the id travels between them;
//  - error: the icon muted with a small mark; ReplayAnnouncer says "Couldn't play this" once, politely.
import { useSyncExternalStore } from "react";
import { useI18n } from "../i18n";
import type { ReplayControl } from "../media/replay";
import { ReplayOriginalIcon } from "./icons";

export function ReplayButton({
  segmentId,
  ready,
  replay,
  reasonId,
}: {
  segmentId: string;
  ready: boolean;
  replay: ReplayControl;
  /** the id of this bubble's ReplayReason row, for aria-describedby when the browser cannot play */
  reasonId: string;
}) {
  const { t } = useI18n();
  useSyncExternalStore(replay.subscribe, replay.version);
  const supported = replay.supported();
  const state = replay.state(segmentId);
  const gone = replay.unavailable(segmentId);
  const shown = !supported ? "unsupported" : gone ? "unavailable" : ready ? state : "waiting";
  const active = state === "loading" || state === "playing";
  const name = shown === "unavailable" ? t("replayUnavailable") : active ? t("replayStop") : t("replay");
  return (
    <button
      type="button"
      className={`icon-button replay ${shown}`}
      aria-label={name}
      aria-pressed={active}
      aria-busy={state === "loading" || undefined}
      aria-describedby={supported ? undefined : reasonId}
      disabled={!supported || !ready || gone}
      data-state={shown}
      onClick={() => replay.toggle(segmentId)}
      data-spec="UI-C5 P-8 D-11"
    >
      <ReplayOriginalIcon />
    </button>
  );
}

/** UI-C5: the row under the meta row that exists only when Replay has something to explain — today, only a
 * browser that cannot play the clip at all. It never wraps into the meta row; it is its own row. */
export function ReplayReason({ replay, id }: { replay: ReplayControl; id: string }) {
  const { t } = useI18n();
  useSyncExternalStore(replay.subscribe, replay.version);
  if (replay.supported()) return null;
  return (
    <p className="replay-reason" id={id}>
      {replay.onIOS() ? t("replayNeedsNewerIOS") : t("replayNeedsNewerBrowser")}
    </p>
  );
}

/** UI-C5 error: ONE polite live region for the canvas; it says "Couldn't play this" while a replay shows its error
 * look, and is empty otherwise (it exists from the start, so the change is announced). */
export function ReplayAnnouncer({ replay }: { replay: ReplayControl }) {
  const { t } = useI18n();
  useSyncExternalStore(replay.subscribe, replay.version);
  return (
    <span className="visually-hidden replay-announcer" role="status" aria-live="polite" data-spec="UI-C5">
      {replay.failing() ? t("replayFailed") : ""}
    </span>
  );
}
